import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { useCallback, useState } from "react";
import styled from "styled-components";
import { Loader, ModalFunds } from "components";
import { useAnchor } from "hooks";
import { Table } from "./Table";
import { FUND_TYPES, Fund, FundTypes } from "./types";
import { useFunds } from "./useFunds";

export type TableFundsProps = {};

const TableFunds = withErrorBoundary(() => {
  const { error, funds, loading } = useFunds();

  const [selectedFund, setSelectedFund] = useState<Fund | undefined>(undefined);

  const handleCloseModal = useCallback(() => setSelectedFund(undefined), []);

  const handleSelectFund = useCallback((fund: Fund) => setSelectedFund(fund), []);

  useAnchor("table-funds");

  if (loading) {
    return (
      <StyledTableFunds className="table-funds Content">
        <Loader className="TableFunds__loader" />
      </StyledTableFunds>
    );
  }

  if (error || !funds || (funds && Object.keys(funds).length === 0)) {
    return null;
  }

  return (
    <StyledTableFunds className="table-funds Content">
      <div className="TableFunds__items">
        {FUND_TYPES.map((fundType: FundTypes, index: number) => {
          const fund = funds?.[fundType];
          // Signify the last fund has rendered (useful for snapshots).
          const lastFund = FUND_TYPES.length - 1 === index;
          const lastFunClassName = lastFund ? "Fund__last" : "";

          if (!fund) {
            return null;
          }

          return (
            <div key={fundType} className="TableFunds__item">
              <h3 className="TableFunds__itemTitle Font_32_40_600">{fundType}</h3>
              <Table
                funds={fund}
                onSelectFund={handleSelectFund}
                className={`TableFunds__itemTable ${lastFunClassName}`}
              />
            </div>
          );
        })}
      </div>
      <ModalFunds isOpen={!!selectedFund} onRequestClose={handleCloseModal} fund={selectedFund} />
    </StyledTableFunds>
  );
}, {});

const StyledTableFunds = styled.section`
  min-height: 50vh;
  margin-top: -64px;
  padding-top: 64px;

  .TableFunds__loader {
    fill: ${({ theme }) => theme.colors.dusk["80"]};
    margin-top: 64px;
  }

  .TableFunds__item {
    display: flex;
  }

  .TableFunds__item:not(:first-child, :last-child) {
    padding: 40px 0;
  }

  .TableFunds__item:first-child {
    padding: 0 0 40px;
  }

  .TableFunds__item:last-child {
    padding: 40px 0 0;
  }

  .TableFunds__itemTitle {
    width: 240px;
    flex-shrink: 0;
  }

  .TableFunds__itemTable {
    margin-left: 32px;
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.xl}px`}) {
    .TableFunds__item {
      flex-direction: column;
    }

    .TableFunds__itemTitle {
      width: 100%;
    }

    .TableFunds__itemTable {
      margin-left: 0;
      margin-top: 16px;
    }
  }
`;

gql`
  fragment TableFundsContent on ComponentTableTableFunds {
    id
  }
`;

export { TableFunds };
